/**
 * Gift With Purchase — product-triggered
 * =============================================================================
 * Auto-adds a free gift line item when a TRIGGER product is in the cart, and
 * removes it when the trigger leaves the cart. The cart UI is re-rendered
 * through the theme's own Section Rendering API path (window.cart.getCart()),
 * never a full reload.
 *
 * PRICING IS NOT HANDLED HERE.
 * The gift's FREE price is enforced by a NATIVE Shopify automatic
 * "Buy X Get Y" discount configured in Admin:
 *     Customer buys  -> the trigger product
 *     Customer gets  -> the gift product, at 100% off (Free)
 * The Ajax Cart API cannot set line prices, and doing so client-side would be
 * insecure and ignored. This script ONLY manages presence + quantity of the
 * gift line item; Shopify applies the discount at render/checkout.
 * =============================================================================
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // CONFIG
  // ---------------------------------------------------------------------------
  var CONFIG = {
    // Master on/off from the theme customizer ("Gift with purchase" section).
    // Defaults to true when the setting isn't present.
    enabled: !(window.theme && theme.giftWithPurchase) || theme.giftWithPurchase.enabled !== false,

    triggerType: 'product', // product-triggered, NOT a subtotal threshold

    // Trigger by PARENT PRODUCT id(s). When any variant of these products is in
    // the cart, the gift qualifies — new variants are covered automatically.
    // Preferred over variant ids; sourced from the trigger product picker.
    triggerProductIds:
      (window.theme && theme.giftWithPurchase && theme.giftWithPurchase.triggerProductIds) || [],

    // Trigger by specific VARIANT id(s). Use when only certain variants of a
    // product should qualify. Combined (OR) with triggerProductIds above.
    triggerVariantIds:
      (window.theme && theme.giftWithPurchase && theme.giftWithPurchase.triggerVariantIds) || [
        /* <TRIGGER_VARIANT_ID> */
      ],

    // The free gift variant (same source pattern as the trigger).
    giftVariantId: (window.theme && theme.giftWithPurchase && theme.giftWithPurchase.variantId) || null,

    giftQuantity: 1, // ALWAYS clamped to 1, regardless of trigger quantity

    // Line-item property used to tag the auto-added gift.
    giftProperty: '_gwp',
  };

  // Normalised numeric sets of trigger ids for fast comparison.
  var TRIGGER_IDS = (CONFIG.triggerVariantIds || []).map(Number);
  var TRIGGER_PRODUCT_IDS = (CONFIG.triggerProductIds || []).map(Number);

  // ---------------------------------------------------------------------------
  // Cart route helpers
  // ---------------------------------------------------------------------------
  var routes = (window.theme && theme.routes) || {};
  var CART_JS_URL = (routes.cart_url || '/cart') + '.js';
  var CART_ADD_URL = routes.cart_add_url || '/cart/add.js';
  var CART_CHANGE_URL = routes.cart_change_url || '/cart/change.js';

  // Header that marks our own cart mutations so the fetch/XHR interceptor can
  // ignore them (prevents reconcile feedback loops on our add/remove).
  var INTERNAL_HEADER = 'X-GWP-Internal';

  // ---------------------------------------------------------------------------
  // Re-render the theme's cart UI via the Section Rendering API.
  // ---------------------------------------------------------------------------
  /**
   * This Archetype Expanse-family theme re-renders the cart through its
   * CartDrawer instance (exposed as `window.cart`). getCart() fetches
   * `?section_id=api-cart-items`, extracts `[data-api-content]`, and rebuilds
   * line items / subtotal / count badge / free-shipping bar via build().
   *
   * window.cart is NOT instantiated on the cart PAGE (only the drawer), so we
   * fall back to fetching the current page's api-cart-items section and
   * swapping `[data-api-content]` ourselves, then dispatching theme:cart:added.
   *
   * NOTE (drawer pops open): getCart() ends in build() -> openCartDrawer(), so
   * the drawer opens on every auto add/remove. That is standard add-to-cart
   * behaviour for this theme — left as-is per spec.
   * TODO (silent background refresh, if you ever want one): suppress the open
   * by short-circuiting openCartDrawer() before calling getCart():
   *     var wasOpen = window.cart.isCartDrawerOpen;
   *     window.cart.isCartDrawerOpen = true;   // openCartDrawer() early-returns
   *     window.cart.getCart();
   *     document.addEventListener('theme:cart:added', function restore() {
   *       window.cart.isCartDrawerOpen = wasOpen;
   *       document.removeEventListener('theme:cart:added', restore);
   *     });
   * This relies on theme internals — confirm before enabling.
   */
  function refreshThemeCartUI() {
    // Primary: drive the theme's own re-render.
    if (window.cart && typeof window.cart.getCart === 'function') {
      window.cart.getCart();
      return Promise.resolve();
    }

    // Fallback: cart page (no window.cart). Re-render the section ourselves.
    return fetch(window.location.pathname + '?section_id=api-cart-items', {
      headers: headers(),
    })
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var holder = document.createElement('div');
        holder.innerHTML = html;

        var fresh = holder.querySelector('[data-api-content]');
        var target = document.querySelector('[data-api-content]');

        if (fresh && target) {
          target.innerHTML = fresh.innerHTML;
        }

        document.dispatchEvent(new CustomEvent('theme:cart:added', {bubbles: true}));
      })
      .catch(function (error) {
        console.error('[GWP] refreshThemeCartUI failed:', error);
      });
  }

  // ---------------------------------------------------------------------------
  // Eligibility + reconciliation
  // ---------------------------------------------------------------------------
  function isTriggerItem(item) {
    // Match on parent product id (any variant) OR a specific variant id.
    return (
      TRIGGER_PRODUCT_IDS.indexOf(Number(item.product_id)) !== -1 ||
      TRIGGER_IDS.indexOf(Number(item.id)) !== -1 ||
      TRIGGER_IDS.indexOf(Number(item.variant_id)) !== -1
    );
  }

  function isGiftItem(item) {
    return Number(item.id) === Number(CONFIG.giftVariantId) || Number(item.variant_id) === Number(CONFIG.giftVariantId);
  }

  function isEligible(cart) {
    if (CONFIG.triggerType !== 'product') return false;
    if (!TRIGGER_PRODUCT_IDS.length && !TRIGGER_IDS.length) return false;
    return cart.items.some(isTriggerItem);
  }

  function findGiftLine(cart) {
    return cart.items.filter(isGiftItem)[0] || null;
  }

  /**
   * Cart subtotal in cents, EXCLUDING the auto-added gift line. Not used by the
   * product trigger, but preserved for threshold-mode reuse and exposed on the
   * public API so downstream code doesn't double-count the free gift.
   */
  function getSubtotalExcludingGift(cart) {
    return cart.items.reduce(function (sum, item) {
      return isGiftItem(item) ? sum : sum + item.line_price;
    }, 0);
  }

  /**
   * Bring the gift line into sync with eligibility. Idempotent: safe to call
   * repeatedly; only mutates the cart when the current state is wrong.
   */
  function reconcile() {
    if (!CONFIG.enabled || !CONFIG.giftVariantId) return Promise.resolve();

    return getCartState().then(function (cart) {
      var eligible = isEligible(cart);
      var giftLine = findGiftLine(cart);

      if (eligible && !giftLine) {
        return addGift();
      }
      if (eligible && giftLine && giftLine.quantity !== CONFIG.giftQuantity) {
        // Clamp the gift back to 1 (e.g. customer bumped its qty).
        return changeLine(giftLine.key, CONFIG.giftQuantity);
      }
      if (!eligible && giftLine) {
        return changeLine(giftLine.key, 0);
      }
      return null; // already in sync — no mutation, no refresh
    });
  }

  function addGift() {
    var body = {
      items: [
        {
          id: Number(CONFIG.giftVariantId),
          quantity: CONFIG.giftQuantity,
          properties: keyedProperty(),
        },
      ],
    };

    return fetch(CART_ADD_URL, {
      method: 'POST',
      headers: headers({json: true}),
      body: JSON.stringify(body),
    })
      .then(check)
      .then(function () {
        return refreshThemeCartUI();
      })
      .catch(function (error) {
        console.error('[GWP] addGift failed:', error);
      });
  }

  function changeLine(key, quantity) {
    return fetch(CART_CHANGE_URL, {
      method: 'POST',
      headers: headers({json: true}),
      body: JSON.stringify({id: key, quantity: quantity}),
    })
      .then(check)
      .then(function () {
        return refreshThemeCartUI();
      })
      .catch(function (error) {
        console.error('[GWP] changeLine failed:', error);
      });
  }

  // ---------------------------------------------------------------------------
  // Mutex — serialise cart mutations so concurrent triggers can't race.
  // While busy, the latest request collapses into a single queued run.
  // ---------------------------------------------------------------------------
  var mutex = {busy: false, queued: false};

  function runReconcile() {
    if (mutex.busy) {
      mutex.queued = true; // coalesce — one trailing run covers all bursts
      return;
    }
    mutex.busy = true;
    Promise.resolve(reconcile())
      .catch(function (error) {
        console.error('[GWP] reconcile failed:', error);
      })
      .then(function () {
        mutex.busy = false;
        if (mutex.queued) {
          mutex.queued = false;
          runReconcile();
        }
      });
  }

  // Debounced entry point — coalesces a burst of cart mutations (e.g. multi-add)
  // into a single reconcile pass.
  var reconcileTimer = null;
  function scheduleReconcile() {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () {
      reconcileTimer = null;
      runReconcile();
    }, 50);
  }

  // ---------------------------------------------------------------------------
  // Intercept cart mutations made by the THEME (and anything else) so we can
  // reconcile after the customer adds/removes products. Our own gift mutations
  // are tagged with INTERNAL_HEADER and skipped to avoid feedback loops.
  // ---------------------------------------------------------------------------
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  function isMutationUrl(url) {
    return typeof url === 'string' && CART_MUTATION_RE.test(url);
  }

  // fetch()
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var internal = hasInternalHeader(init && init.headers) || (input && hasInternalHeader(input.headers));
      var result = nativeFetch.apply(this, arguments);

      if (!internal && isMutationUrl(url)) {
        result.then(function () { scheduleReconcile(); }).catch(function () {});
      }
      return result;
    };
  }

  // XMLHttpRequest (theme add-to-cart uses XHR in some flows)
  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__gwpUrl = url;
    return nativeOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    if (isMutationUrl(xhr.__gwpUrl) && !xhr.__gwpInternal) {
      xhr.addEventListener('load', function () {
        scheduleReconcile();
      });
    }
    return nativeSend.apply(this, arguments);
  };

  // ---------------------------------------------------------------------------
  // Small utilities
  // ---------------------------------------------------------------------------
  function getCartState() {
    return fetch(CART_JS_URL, {headers: headers()}).then(function (response) {
      return response.json();
    });
  }

  function headers(opts) {
    var h = {};
    h[INTERNAL_HEADER] = '1'; // mark every request we originate as internal
    h['X-Requested-With'] = 'XMLHttpRequest';
    if (opts && opts.json) {
      h['Content-Type'] = 'application/json';
      h['Accept'] = 'application/json';
    }
    return h;
  }

  function hasInternalHeader(h) {
    if (!h) return false;
    if (typeof h.get === 'function') return !!h.get(INTERNAL_HEADER); // Headers instance
    return !!(h[INTERNAL_HEADER] || h[INTERNAL_HEADER.toLowerCase()]);
  }

  function keyedProperty() {
    var p = {};
    p[CONFIG.giftProperty] = 'true';
    return p;
  }

  function check(response) {
    if (!response.ok) {
      return response.text().then(function (text) {
        throw new Error('Cart request failed (' + response.status + '): ' + text);
      });
    }
    return response;
  }

  // ---------------------------------------------------------------------------
  // Init — sync once on load (handles trigger products already in the cart).
  // ---------------------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runReconcile);
  } else {
    runReconcile();
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------
  window.giftWithPurchase = Object.assign(window.giftWithPurchase || {}, {
    config: CONFIG,
    reconcile: runReconcile,
    refreshThemeCartUI: refreshThemeCartUI,
    isEligible: isEligible,
    getSubtotalExcludingGift: getSubtotalExcludingGift,
  });
})();
